import { useQueryClient } from '@tanstack/react-query';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Controls, ControlsButton } from '@/components/Controls';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';
import { EditorialFacebookPostNetwork } from '@/utils/enums';

const StyledControls = styled(Controls)`
  border-color: ${({ theme }) => theme.colors.grayscale};
  position: relative;
  margin-left: auto;
`;

const Label = styled(Text)`
  position: absolute;
  left: 0;
  bottom: calc(100% + 7px);
`;

interface Props {
  activePage: EditorialQueuePage;
}

const AutoDeleteSwitch: FC<Props> = ({ activePage }) => {
  const alerts = useAlerts();
  const toasts = useToasts();
  const queryClient = useQueryClient();
  const { network } = useParams();

  const settings = useMemo(() => {
    const n =
      network === 'instagram'
        ? EditorialFacebookPostNetwork.INSTAGRAM
        : EditorialFacebookPostNetwork.FACEBOOK;

    return activePage?.settings.find((s) => s.network === n);
  }, [activePage?.settings, network]);

  const onModeChange = async (mode: string | null) => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure?',
        `Delete mode: ${mode ? mode.toUpperCase() : 'OFF'}`,
      );

      if (!confirmed) {
        return;
      }

      await api.post(`/v1/editorial/queue/settings`, {
        auto_delete_mode: mode,
        network:
          network === 'instagram'
            ? EditorialFacebookPostNetwork.INSTAGRAM
            : EditorialFacebookPostNetwork.FACEBOOK,
        page_id: activePage.id,
      });

      await queryClient.invalidateQueries([
        'editorial_distribution_queue_channels',
      ]);

      alerts.close();

      toasts.create('success', 'Saved');
    } catch (_) {
      alerts.error();
    }

    return;
  };

  return (
    <StyledControls>
      <Label as="span" $type="smallHeadline">
        Post Delete:
      </Label>
      <ControlsButton
        gray
        active={!settings?.autoDeleteMode}
        onClick={() => onModeChange(null)}
      >
        Off
      </ControlsButton>
      <ControlsButton
        active={settings?.autoDeleteMode === 'auto'}
        onClick={() => onModeChange('auto')}
      >
        Auto
      </ControlsButton>
      <ControlsButton
        active={settings?.autoDeleteMode === 'manual'}
        onClick={() => onModeChange('manual')}
      >
        Manual
      </ControlsButton>
    </StyledControls>
  );
};

export default AutoDeleteSwitch;
